import type { PaymentPageForm } from "../../types/paymentPage";
import { parseValue } from "../paymentLink/components/form/formStore";
import type { Pp2Link } from "./shared/links";
import { bannerUrl, emailLink, isHttpUrl, mapsUrl, phoneLink, trimmedOrNull, websiteLink } from "./shared/links";
import type { LondonNow, OpeningHoursInfo } from "./shared/openingHours";
import { buildOpeningHours } from "./shared/openingHours";

export type { LondonNow, OpeningHoursInfo, OpeningHoursRow } from "./shared/openingHours";

/** Everything the pp2 layout and its blocks read (spec section 1, "Shared contract"). */
export type Pp2ViewModel = {
  /** tradingName || beneficiaryName || "" */
  displayName: string;
  /** beneficiaryName || "" */
  holderName: string;
  /** trimmed, null if empty */
  businessType: string | null;
  heroUrl: string | null;
  logoUrl: string | null;
  /** null when no day has valid hours (spec 5.1) */
  hours: OpeningHoursInfo | null;
  phone: Pp2Link | null;
  email: Pp2Link | null;
  website: Pp2Link | null;
  /** !!(hours || phone || email || website) */
  hasQuickContacts: boolean;
  /** amount > 0 (spec 5.2) */
  amountFixed: boolean;
  /** !!paymentLinkDescription?.trim() */
  descriptionFixed: boolean;
  /** allowBankTransfers */
  showBankButton: boolean;
  /** showPayByCard */
  showCardButton: boolean;
  bank: { holder: string; sortCode: string; accountNumber: string; copyText: string };
  /** paymentPageDescription, trimmed */
  about: string | null;
  location: { address: string | null; placeId: string | null; mapsUrl: string } | null;
  social: { instagram: string | null; facebook: string | null; linkedin: string | null } | null;
  /** count of non-null: about, location, hours, social (0..4) */
  sideBlockCount: number;
  /** sideBlockCount >= 2; only takes effect on desktop */
  twoColumnOnDesktop: boolean;
  bannerUrl: string;
};

const TWO_COLUMN_MIN_SIDE_BLOCKS = 2;

/** A stored social link, kept only when it is an http(s) URL (never e.g. `javascript:`). */
const socialLink = (value: Nullable<string>): string | null => {
  const link = trimmedOrNull(value);
  return link && isHttpUrl(link) ? link : null;
};

export function buildPp2ViewModel(page: PaymentPageForm, now: LondonNow): Pp2ViewModel {
  const displayName = trimmedOrNull(page.tradingName) ?? trimmedOrNull(page.beneficiaryName) ?? "";
  const holder = page.beneficiaryName ?? "";
  const sortCode = page.beneficiarySortCode ?? "";
  const accountNumber = page.beneficiaryAccountNumber ?? "";

  const hours = buildOpeningHours(page.workingHours, now);
  const phone = trimmedOrNull(page.contactPhone);
  const email = trimmedOrNull(page.contactEmail);
  const website = trimmedOrNull(page.contactWebsite);

  const about = trimmedOrNull(page.paymentPageDescription);
  const address = trimmedOrNull(page.address);
  const placeId = trimmedOrNull(page.googleMapsPlaceId);
  const location = address || placeId ? { address, placeId, mapsUrl: mapsUrl(address, displayName, placeId) } : null;

  const instagram = socialLink(page.instagramLink);
  const facebook = socialLink(page.facebookLink);
  const linkedin = socialLink(page.linkedinLink);
  const social = instagram || facebook || linkedin ? { instagram, facebook, linkedin } : null;

  const sideBlockCount = [about, location, hours, social].filter((block) => block !== null).length;

  return {
    displayName,
    holderName: holder,
    businessType: trimmedOrNull(page.businessType),
    heroUrl: trimmedOrNull(page.backgroundImage),
    logoUrl: trimmedOrNull(page.logo),
    hours,
    phone: phone ? phoneLink(phone) : null,
    email: email ? emailLink(email) : null,
    website: website ? websiteLink(website) : null,
    hasQuickContacts: !!(hours || phone || email || website),
    amountFixed: parseValue(page.amount || "") > 0,
    descriptionFixed: !!page.paymentLinkDescription?.trim(),
    showBankButton: page.allowBankTransfers,
    showCardButton: page.showPayByCard,
    bank: {
      holder,
      sortCode,
      accountNumber,
      copyText: `${holder}\nSort code: ${sortCode}\nAccount#: ${accountNumber}`,
    },
    about,
    location,
    social,
    sideBlockCount,
    twoColumnOnDesktop: sideBlockCount >= TWO_COLUMN_MIN_SIDE_BLOCKS,
    bannerUrl: bannerUrl(page.publicId ?? null),
  };
}
